import { ComponentProps, FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from '../Modal';
import styles from './CreateMalfuntion.module.css';
import { Input } from '../TextField/views';
import { Button } from '../Button';
import { Select } from '../Select';
import { AutoExpandTextArea } from '../AutoExpandTextArea';
import { ImageInput } from './ImageInput';
import { Form, Formik } from 'formik';
import { FormField } from '../FormField';
import { TypeIds } from '../../constants/malfunction';
import { Values } from './types';
import { schema } from './schema';
import clsx from 'clsx';
import { useVehicle } from '../../services/vehicle';
import { submitMalfunction, useSubmitState } from './service';
import { Spinner } from '../Spinner';
import { ProgressCircle } from '../ProgressCircle';

interface CreateMalfunctionProps {
  open: boolean;
  onClose: () => void;
}

export const CreateMalfunction: FC<CreateMalfunctionProps> = (props) => {
  const { $t } = useIntl();
  const { result: vehicle } = useVehicle();

  const options: ComponentProps<typeof Select>['options'] = Array.from(
    TypeIds
  ).map(([value, label]) => ({
    value,
    label: $t({ id: label }),
  }));

  const uploadState = useSubmitState();

  return (
    <Modal title={<FormattedMessage id="Malfunctions.New" />} {...props}>
      <Formik<Values>
        initialValues={{
          name: '',
          vehicle: {
            value: vehicle?.id ?? null,
            label: vehicle?.name ?? '',
          } as any,
          type: null as any,
          description: '',
          files: [],
        }}
        onSubmit={(values) => submitMalfunction(values)}
        validationSchema={schema}
        validateOnMount
      >
        {({ setFieldValue, setFieldTouched, isValid, touched, errors }) => (
          <Form className={styles.form} data-testid="create-malfunction-dialog">
            <FormField<typeof Input>
              component={Input}
              id="malfunction-name"
              name="name"
              label={$t({ id: 'Malfunction.Name' })}
              required
            />
            <FormField<typeof Input>
              component={Input}
              id="malfunction-vehicle"
              name="vehicle.label"
              label={$t({ id: 'Malfunction.Vehicle' })}
              disabled
              required
            />
            <FormField<typeof Select>
              component={Select}
              id="malfunction-type"
              name="type"
              label={$t({ id: 'Malfunction.Type' })}
              options={options}
              isClearable
              onChange={(o) =>
                setFieldTouched('type').then(() => setFieldValue('type', o))
              }
              onBlur={() => setFieldTouched('type')}
              required
            />
            <label
              htmlFor="malfunction-description"
              className={clsx(styles.label, {
                [styles.error]: !!touched.files && !!errors.files,
              })}
            >
              <span>
                <FormattedMessage id="Malfunction.Description" />:
              </span>
              <div className={styles.textarea}>
                <FormField<typeof AutoExpandTextArea>
                  component={AutoExpandTextArea}
                  placeholder={`${$t({
                    id: 'Malfunction.Description.Placeholder',
                  })}...`}
                  name="description"
                />
                <hr className={styles.divider} />
                <ImageInput
                  onChange={(files) =>
                    setFieldTouched('files').then(() =>
                      setFieldValue('files', files)
                    )
                  }
                />
              </div>
              {!!touched.files && !!errors.files && (
                <FormattedMessage
                  tagName="sup"
                  id={
                    typeof errors.files === 'string'
                      ? errors.files
                      : errors.files[0]
                  }
                />
              )}
            </label>
            {uploadState?.error && (
              <div className={styles.message}>
                <FormattedMessage id="Submit.Error" />
              </div>
            )}
            <Button type="submit" className={styles.button} disabled={!isValid}>
              <FormattedMessage id="Malfunction.Create" />
            </Button>
          </Form>
        )}
      </Formik>
      {uploadState?.running && (
        <div className={styles.backdrop}>
          <div>
            {uploadState.total ? (
              <ProgressCircle {...uploadState} />
            ) : (
              <Spinner />
            )}
            <FormattedMessage tagName="div" id="Uploading">
              {(node) => <>{node}...</>}
            </FormattedMessage>
          </div>
        </div>
      )}
    </Modal>
  );
};
